import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnnouncementNotificationService } from '../services/announcement-notification.service';

/**
 * One-per-session banner telling the user they have unread Normal-priority
 * announcements. High priority supersedes the banner with the blocking dialog
 * (`ri-announcement-gate`); Low priority is bell-only. Driven by `bannerCount$`, which
 * already excludes both.
 *
 * Lives in the app shell rather than on the home page: the whole point is to reach
 * someone who never opens the home page because they work from a bookmarked deep
 * link. The persistent bell badge in the toolbar is the standing reminder — this is
 * the arrival nudge.
 *
 * Dismissal is a baseline rather than a boolean: the banner is hidden while the
 * count is at or below the count it was dismissed at, and returns as soon as
 * something new pushes it above. The baseline also follows the count *down* as the
 * user reads, so dismissing at 3 and then reading one does not leave a stale
 * high-water mark that silently swallows the next announcement.
 */
@Component({
  selector: 'ri-announcement-banner',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './announcement-banner.component.html',
  styleUrls: ['./announcement-banner.component.scss']
})
export class AnnouncementBannerComponent implements OnInit, OnDestroy {
  /**
   * Session-scoped, so the nudge returns on the next sign-in but not on every
   * navigation. Holds the unread count at the moment of dismissal, not a flag.
   */
  private static readonly DISMISSED_AT_KEY = 'ri.announcementsBannerDismissedAt';

  count = 0;

  /** Unread count the banner was last dismissed at; anything above this shows again. */
  private dismissedAt = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly notifications: AnnouncementNotificationService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.dismissedAt = this.readDismissedAt();

    this.notifications.bannerCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.count = count;

        // Track the count downwards as the user reads, so the next arrival clears
        // the baseline and the banner returns.
        if (count < this.dismissedAt) {
          this.dismissedAt = count;
          this.writeDismissedAt();
        }

        this.cdr.markForCheck();
      });
  }

  get visible(): boolean {
    return this.count > this.dismissedAt;
  }

  onRead(): void {
    this.dismiss();
    this.router.navigate(['/announcements/my']);
  }

  dismiss(): void {
    this.dismissedAt = this.count;
    this.writeDismissedAt();
    this.cdr.markForCheck();
  }

  /**
   * Storage access throws outright in some privacy modes, so a failure to read or
   * write the baseline must never take the shell down — worst case the banner shows
   * again on the next navigation.
   */
  private readDismissedAt(): number {
    try {
      const stored = Number(sessionStorage.getItem(AnnouncementBannerComponent.DISMISSED_AT_KEY));
      return Number.isFinite(stored) && stored > 0 ? stored : 0;
    } catch {
      return 0;
    }
  }

  private writeDismissedAt(): void {
    try {
      sessionStorage.setItem(
        AnnouncementBannerComponent.DISMISSED_AT_KEY, String(this.dismissedAt));
    } catch {
      // Ignored on purpose — see readDismissedAt().
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
